import * as React from 'react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import Carousel from 'nuka-carousel';
import { useLocation, useNavigate } from 'react-router';

import { IconLocked } from 'components/icons/locked';
import { IconLoyaltyPoints } from 'components/icons/loyalty-points-icon';
import { IncentiveEvaluationMessages } from 'components/incentive-evaluation-messages/incentive-evaluation-messages';
import { Picture } from 'components/picture';
import { NextButton, PreviousButton } from 'components/rewards-carousel/rewards-carousel.styled';
import { IRewardFragment } from 'generated/graphql-gateway';
import { useFeatureLoyaltyUi } from 'hooks/use-feature-loyalty-ui';
import { useWindowDimensions } from 'hooks/use-window-dimensions';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames } from 'state/cdp/constants';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useFeaturesLoyaltyTiersContent } from 'state/loyalty/hooks/use-feature-loyalty-tiers-content';
import { useLoyaltyRewards } from 'state/loyalty/hooks/use-loyalty-rewards';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { isTestOnly } from 'state/loyalty/utils';
import { primitive } from 'styles/constants/primitives';
import { isMobile } from 'utils/environment';
import { scrollContainerToChild } from 'utils/scroll';

import { LoyaltyIncentivesGrid } from '../loyalty-incentives-components';
import { IncentiveCard } from '../loyalty-incentives-components/incentive-card/incentive-card';
import { REWARD_CARD_HEIGHT } from '../loyalty-incentives-components/incentive-card/incentive-card.styled';
import { parseIncentiveData } from '../loyalty-incentives-components/incentive-card/parse-incentive-data';
import { RewardCardContent } from '../loyalty-incentives-components/incentive-card/reward-card-content';

import { CART_POINTS_LABEL_ID, POINTS_LABEL_ID } from './constants';
import {
  AccordionTrigger,
  AddRewardCTA,
  CATEGORY_TRANSITION_TIME,
  CardWrapper,
  CardWrapperInline,
  CarouselContainer,
  CarouselContainerInline,
  CarouselHeading,
  CarouselHeadingContainer,
  CarouselItem,
  CarouselItemInline,
  CarouselRewardName,
  CarouselRewardNameInline,
  CarouselSection,
  CarouselViewMoreItem,
  CategoryHeader,
  CategoryImage,
  CategoryLabel,
  DelayedUnmountRewardContainer,
  InlineRewardsCarouselContainer,
  ItemLoyaltyIcon,
  LockOverlay,
  LockOverlayInline,
  LoyaltyRewardsTitle,
  NextButtonInline,
  PictureWrapper,
  PictureWrapperInline,
  PointsNeededText,
  PreviousButtonInline,
  RewardCategoryAccordion,
  RewardPictureLock,
  RewardPictureLockInline,
  StyledPointsDisplayBadge,
  StyledPointsDisplayBadgeInline,
  StyledPointsDisplayBadgeLocked,
  StyledPointsDisplayBadgeLockedInline,
  StyledPointsDisplayHeading,
  StyledRewardsCarousel,
  ViewMoreCardWrapper,
  ViewMoreText,
} from './loyalty-rewards.styled';
import { ViewMoreRewardIcon } from './loyalty-view-more-reward-icon';
import { ILoyaltyRewardsViewProps } from './types';

type Reward = {
  readonly __typename?: 'Reward';
  readonly _id: string;
  readonly loyaltyEngineId: string | null;
} | null;

export const LoyaltyRewardsViewBase: FC<ILoyaltyRewardsViewProps> = ({
  handleClick,
  sanityRewardsMap,
  selectedCategories,
  setSelectedCategories,
  engineRewardsMap,
  formatMessage,
  selectedReward,
  rewardsTabPath = '',
  rewardCategories,
  refetchRewards,
  rewardsEvaluationMap,
  title,
  isWidget,
  isCarousel,
  isInline,
  currentUserTier,
  setSelectedRewardIdFromWidget,
}) => {
  const { getContentForTier } = useFeaturesLoyaltyTiersContent();
  const { state } = useLocation();

  const rewardsRoute = `${formatMessage({ id: 'routes.loyaltyHome' })}/${rewardsTabPath}`;
  const [lastSelectedCategory, setLastSelectedCategory] = useState('');
  const lastSelectedCategoryRef = useRef<HTMLLIElement | null>();
  const [scrollTimeoutId, setScrollTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const lockOnImageEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOCK_ON_REWARD_IMAGE));
  const { loyaltyUser } = useLoyaltyUser();
  const { isRewardTierLocked } = useLoyaltyRewards(loyaltyUser);
  const userPoints = loyaltyUser?.points || 0;

  const { width: innerWidth } = useWindowDimensions();

  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const setScrollContainerRef = (e: HTMLElement | null) => {
    scrollContainerRef.current = e;
  };

  const { featureLoyaltyUi } = useFeatureLoyaltyUi();
  const rewardsPath =
    featureLoyaltyUi?.loyaltyCTAButtons?.loyaltyBannerHomepageAuthenticatedButton
      ?.loyaltyBannerHomepageAuthenticatedLink?.locale ||
    formatMessage({ id: 'routes.loyaltyDashboard' });

  const rewardLabel = useCallback(
    (engineReward: IRewardFragment) => {
      // If reward has Loyalty Tier requirement and user tier is different show required tier in label
      if (isRewardTierLocked(engineReward, currentUserTier)) {
        return getContentForTier(engineReward.requiredLoyaltyTier!)?.loyaltyTiersUIConfigName
          ?.locale;
      }

      // If reward does not have points, it's free
      if (!engineReward.pointCost) {
        return formatMessage({ id: 'loyaltyFree' });
      }

      return engineReward.pointCost.toString();
    },
    [formatMessage, getContentForTier, isRewardTierLocked]
  );

  const navigate = useNavigate();
  const { logNavigationClick } = useCdpContext();
  const { location } = useLocationContext();

  const handleViewMoreClick = () => {
    logNavigationClick(CustomEventNames.BUTTON_CLICK, {
      Name: 'View More Rewards',
      CurrentPath: location.pathname,
      PathTo: rewardsPath,
    });

    navigate(rewardsPath);
  };

  const carouselCardMapper = (reward: any) => {
    if (reward?._id === 'view-all-card') {
      return (
        <CarouselViewMoreItem key="view-all-card" onClick={handleViewMoreClick}>
          <ViewMoreCardWrapper>
            <ViewMoreText>{formatMessage({ id: 'rewardsViewMore' })}</ViewMoreText>
            <ViewMoreRewardIcon />
          </ViewMoreCardWrapper>
        </CarouselViewMoreItem>
      );
    }

    const rewardId = reward?._id;
    const incentive = sanityRewardsMap[rewardId || ''];

    const loyaltyRewardId = incentive?.loyaltyEngineId || '';
    const engineReward = engineRewardsMap[loyaltyRewardId];

    // Reward is locked when user does not have enough points or it belongs to a different tier
    const rewardLocked =
      userPoints < engineReward.pointCost || isRewardTierLocked(engineReward, currentUserTier);

    if (!incentive || isTestOnly(incentive)) {
      return null;
    }

    const incentiveData = parseIncentiveData(incentive);
    const incentiveSanityId = incentiveData.id;
    const incentiveName = incentiveData.name;
    const loyaltyEngineId = incentiveData.loyaltyEngineId;
    const rewardCategory = rewardCategories.find(({ rewards }) =>
      rewards?.some(reward => reward?.loyaltyEngineId === loyaltyRewardId)
    )?.label?.locale!;

    const handleRewardClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();
      handleClick({
        incentiveName,
        loyaltyEngineId,
        incentiveSanityId,
        rewardLocked,
        rewardCategory,
      })();
    };

    return isInline ? (
      <CarouselItemInline
        data-testid={`reward-${rewardLocked ? 'locked' : reward._id}`}
        key={rewardId}
        onClick={handleRewardClick}
      >
        <CardWrapperInline locked={rewardLocked}>
          <AddRewardCTA onClick={handleRewardClick}>
            <Icon icon="add" color="white" aria-hidden width="16px" height="16px" />
          </AddRewardCTA>
          <PictureWrapperInline>
            <Picture
              image={incentiveData.image}
              alt={incentiveData.imageDescription}
              objectFitContain={true}
              className="inline-picture"
            />
            {rewardLocked && <LockOverlayInline />}
            {rewardLocked && <RewardPictureLockInline fill={primitive.$white} />}
          </PictureWrapperInline>
          <CarouselRewardNameInline>
            {incentiveData.name} {incentiveData.metadata as unknown as string}
          </CarouselRewardNameInline>

          {rewardLocked ? (
            <StyledPointsDisplayBadgeLockedInline
              value={Number(rewardLabel(engineReward))}
              icon={<IconLocked />}
            />
          ) : (
            <StyledPointsDisplayBadgeInline
              value={Number(rewardLabel(engineReward))}
              icon={<ItemLoyaltyIcon />}
            />
          )}
        </CardWrapperInline>
      </CarouselItemInline>
    ) : (
      <CarouselItem
        data-testid={`reward-${rewardLocked ? 'locked' : reward._id}`}
        key={rewardId}
        onClick={() => {
          if (userPoints < engineReward.pointCost) {
            return;
          }
          handleClick({
            incentiveName,
            loyaltyEngineId,
            incentiveSanityId,
            rewardLocked,
            rewardCategory,
          })();
        }}
      >
        <CardWrapper locked={rewardLocked}>
          <PictureWrapper>
            <Picture
              image={incentiveData.image}
              alt={incentiveData.imageDescription}
              objectFitContain={true}
            />
            {rewardLocked && <LockOverlay />}
            {rewardLocked && <RewardPictureLock fill={primitive.$white} />}
          </PictureWrapper>
          <CarouselRewardName>
            {incentiveData.name} {incentiveData.metadata as unknown as string}
          </CarouselRewardName>

          {rewardLocked ? (
            <StyledPointsDisplayBadgeLocked
              value={Number(rewardLabel(engineReward))}
              icon={<IconLocked />}
            />
          ) : (
            <StyledPointsDisplayBadge
              value={Number(rewardLabel(engineReward))}
              icon={<ItemLoyaltyIcon />}
            />
          )}
        </CardWrapper>
      </CarouselItem>
    );
  };

  useEffect(() => {
    // When used as widget in the reward dashboard, we don't want to add/remove bottom margin
    if (isWidget || isInline) {
      return;
    }
    // A timeout with `CATEGORY_TRANSITION_TIME` is needed because after the transition ends,
    // the height of the window changes, affecting the scroll position
    const currentScrollTimeoutId = global.setTimeout(() => {
      if (lastSelectedCategoryRef.current && scrollContainerRef.current) {
        scrollContainerToChild(scrollContainerRef.current, lastSelectedCategoryRef.current);
      }

      setScrollTimeoutId(null);
    }, CATEGORY_TRANSITION_TIME);

    setScrollTimeoutId(currentScrollTimeoutId);

    return () => clearTimeout(currentScrollTimeoutId);
  }, [isWidget, lastSelectedCategory, setScrollTimeoutId, isInline]);

  const [inlineRewards, setInlineRewards] = useState<Reward[]>([]);
  useEffect(() => {
    let finalRewards: Reward[] = [];
    if (rewardCategories?.length) {
      rewardCategories.forEach(category => {
        if (!!category.rewards) {
          finalRewards = [...finalRewards, ...category.rewards];
        }
      });
    }
    const displayedRewards = finalRewards.slice(0, 10);
    const rewardsWithViewAll: Reward[] = [
      ...displayedRewards,
      {
        _id: 'view-all-card',
        loyaltyEngineId: null,
        __typename: 'Reward',
      },
    ];
    setInlineRewards(rewardsWithViewAll);
  }, [rewardCategories]);

  if (isInline) {
    const minimumSlideWidth = 230;
    const totalOfSlides = inlineRewards.length;
    const isLessThanScreenWidth = minimumSlideWidth * totalOfSlides < innerWidth;
    const slidesToShow = isLessThanScreenWidth
      ? totalOfSlides
      : Math.floor(innerWidth / minimumSlideWidth);
    return (
      <LoyaltyIncentivesGrid
        selectedIncentive={selectedReward}
        refetch={refetchRewards}
        goBack={state?.backRoute || rewardsRoute}
        engineIncentivesMap={engineRewardsMap}
        setScrollContainerRef={setScrollContainerRef}
        offersInCooldown={false}
        isWidget={isWidget}
        isCarousel={isCarousel}
        isInline={isInline}
        setSelectedRewardIdFromWidget={setSelectedRewardIdFromWidget}
      >
        {isMobile() ? (
          <InlineRewardsCarouselContainer>
            <StyledRewardsCarousel>{inlineRewards.map(carouselCardMapper)}</StyledRewardsCarousel>
          </InlineRewardsCarouselContainer>
        ) : (
          <CarouselContainerInline role="region">
            <Carousel
              slideWidth={'12rem'}
              slidesToScroll={1}
              slidesToShow={slidesToShow}
              withoutControls={isLessThanScreenWidth}
              renderBottomCenterControls={null}
              renderCenterLeftControls={({ previousSlide, currentSlide }) => (
                <PreviousButtonInline onClick={previousSlide} disabled={currentSlide === 0} />
              )}
              renderCenterRightControls={({ nextSlide, slideCount, currentSlide }) => {
                const disabled = slideCount - slidesToShow <= currentSlide;
                return <NextButtonInline onClick={nextSlide} disabled={disabled} />;
              }}
            >
              {inlineRewards.map(carouselCardMapper)}
            </Carousel>
          </CarouselContainerInline>
        )}
      </LoyaltyIncentivesGrid>
    );
  }

  if (isCarousel) {
    return (
      <LoyaltyIncentivesGrid
        selectedIncentive={selectedReward}
        refetch={refetchRewards}
        goBack={state?.backRoute || rewardsRoute}
        engineIncentivesMap={engineRewardsMap}
        setScrollContainerRef={setScrollContainerRef}
        offersInCooldown={false}
        isWidget={isWidget}
        isCarousel={isCarousel}
        setSelectedRewardIdFromWidget={setSelectedRewardIdFromWidget}
      >
        {title && <LoyaltyRewardsTitle>{title}</LoyaltyRewardsTitle>}
        {rewardCategories.map(category => {
          const categoryLabel = category.label?.locale;
          if (!category.rewards?.length || !categoryLabel) {
            return null;
          }

          const categoryPoints = category.rewards[0]?.loyaltyEngineId
            ? engineRewardsMap[category.rewards[0]?.loyaltyEngineId].pointCost
            : parseInt(categoryLabel, 10);

          const formattedCategoryLabelId = categoryLabel.replace(/\s/g, '_');
          const categoryHeaderId = `${formattedCategoryLabelId}-header`;
          const categorySectionId = `${formattedCategoryLabelId}-section`;
          const categoryTitle = categoryLabel.split(' ');

          const minimumSlideWidth = 220;

          const totalOfSlides = category.rewards.length;
          // when all slides width is less than innerWidth: hide controls and show all slides
          const isLessThanScreenWidth = minimumSlideWidth * totalOfSlides < innerWidth;
          const slidesToShow = isLessThanScreenWidth
            ? totalOfSlides
            : Math.floor(innerWidth / minimumSlideWidth);

          return (
            <CarouselSection key={categoryLabel}>
              <CarouselHeadingContainer>
                <CarouselHeading>
                  <StyledPointsDisplayHeading
                    value={categoryTitle[0]}
                    suffix={formatMessage({ id: CART_POINTS_LABEL_ID })}
                    icon={userPoints < categoryPoints ? <IconLocked /> : <IconLoyaltyPoints />}
                  />
                  {userPoints < categoryPoints && (
                    <PointsNeededText>
                      {formatMessage(
                        { id: 'yourXPointsAway' },
                        {
                          points: <span>{categoryPoints - userPoints}</span>,
                          pointsSlang: formatMessage({ id: POINTS_LABEL_ID }),
                        }
                      )}
                    </PointsNeededText>
                  )}
                </CarouselHeading>
              </CarouselHeadingContainer>
              {isMobile() ? (
                <StyledRewardsCarousel data-testid={categorySectionId}>
                  {category.rewards.map(carouselCardMapper)}
                </StyledRewardsCarousel>
              ) : (
                <CarouselContainer
                  id={categorySectionId}
                  data-testid={categorySectionId}
                  role="region"
                  aria-labelledby={categoryHeaderId}
                >
                  <Carousel
                    slideWidth={'12rem'}
                    slidesToScroll={1}
                    slidesToShow={slidesToShow}
                    withoutControls={isLessThanScreenWidth}
                    renderBottomCenterControls={null}
                    renderCenterLeftControls={({ previousSlide, currentSlide }) => (
                      <PreviousButton onClick={previousSlide} disabled={currentSlide === 0} />
                    )}
                    renderCenterRightControls={({ nextSlide, slideCount, currentSlide }) => (
                      <NextButton
                        onClick={nextSlide}
                        disabled={slideCount - slidesToShow <= currentSlide}
                      />
                    )}
                  >
                    {category.rewards.map(carouselCardMapper)}
                  </Carousel>
                </CarouselContainer>
              )}
            </CarouselSection>
          );
        })}
      </LoyaltyIncentivesGrid>
    );
  }

  return (
    <LoyaltyIncentivesGrid
      selectedIncentive={selectedReward}
      refetch={refetchRewards}
      goBack={rewardsRoute}
      engineIncentivesMap={engineRewardsMap}
      setScrollContainerRef={setScrollContainerRef}
      offersInCooldown={false}
      isWidget={isWidget}
      setSelectedRewardIdFromWidget={setSelectedRewardIdFromWidget}
    >
      {title && <LoyaltyRewardsTitle>{title}</LoyaltyRewardsTitle>}
      {rewardCategories.map(category => {
        const categoryLabel = category.label?.locale;
        if (!category.rewards?.length || !categoryLabel) {
          return null;
        }

        const isCategorySelected = selectedCategories.includes(categoryLabel);

        const categoryHeight = category.rewards.length * REWARD_CARD_HEIGHT;
        const onClickCategoryHeader = () => {
          if (scrollTimeoutId) {
            // This will most likely not happen, but handle the case where
            // more than 1 scroll is queued up
            clearTimeout(scrollTimeoutId);
            setScrollTimeoutId(null);
          }

          const lastSelectedCat = !isCategorySelected ? categoryLabel : '';
          setLastSelectedCategory(lastSelectedCat);

          return setSelectedCategories(selected => {
            const entries = new Set(selected);
            if (entries.has(categoryLabel)) {
              entries.delete(categoryLabel);
            } else {
              entries.add(categoryLabel);
            }
            return Array.from(entries);
          });
        };

        const isCurrentSelection = lastSelectedCategory === categoryLabel;

        const formattedCategoryLabelId = categoryLabel.replace(/\s/g, '_');
        const categoryHeaderId = `${formattedCategoryLabelId}-header`;
        const categorySectionId = `${formattedCategoryLabelId}-section`;
        return (
          <RewardCategoryAccordion
            open={isCategorySelected}
            isCurrentSelection={isCurrentSelection}
            categoryHeight={categoryHeight}
            key={categoryLabel}
            ref={e => {
              if (isCurrentSelection) {
                lastSelectedCategoryRef.current = e;
              }
            }}
          >
            <CategoryHeader>
              <AccordionTrigger
                id={categoryHeaderId}
                aria-controls={categorySectionId}
                aria-expanded={isCategorySelected}
                onClick={onClickCategoryHeader}
              >
                {category?.image?.locale && (
                  <CategoryImage
                    alt={category.imageDescription?.locale || ''}
                    image={category.image.locale}
                    objectFitContain
                  />
                )}
                <CategoryLabel>{categoryLabel}</CategoryLabel>
                <Icon icon="select" color="icon-default" width="24px" aria-hidden />
              </AccordionTrigger>
            </CategoryHeader>
            <DelayedUnmountRewardContainer
              id={categorySectionId}
              role="region"
              aria-labelledby={categoryHeaderId}
              isVisible={isCategorySelected}
              delayTime={CATEGORY_TRANSITION_TIME}
            >
              {category.rewards.map(reward => {
                const rewardId = reward?._id;
                const incentive = sanityRewardsMap[rewardId || ''];
                if (isTestOnly(incentive)) {
                  return null;
                }
                const loyaltyRewardId = incentive?.loyaltyEngineId || '';
                const engineReward = engineRewardsMap[loyaltyRewardId];
                const rewardEvaluationResults = rewardsEvaluationMap[loyaltyRewardId];
                return (
                  incentive && (
                    <IncentiveCard
                      key={rewardId}
                      incentiveData={parseIncentiveData(incentive)}
                      handleClick={handleClick}
                      locked={lockOnImageEnabled && !!engineReward?.locked}
                    >
                      <RewardCardContent engineReward={engineReward}></RewardCardContent>
                      <IncentiveEvaluationMessages
                        incentiveEvaluationResults={rewardEvaluationResults}
                        hideIcon
                      />
                    </IncentiveCard>
                  )
                );
              })}
            </DelayedUnmountRewardContainer>
          </RewardCategoryAccordion>
        );
      })}
    </LoyaltyIncentivesGrid>
  );
};
